import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { clientsApi } from '../api/clients'
import { actionsApi, type OpenAction } from '../api/actions'
import type { Client } from '../lib/types'
import { clientColor } from '../lib/clientColor'
import { ActionIcons } from '../components/ActionIcons'
import { ActionQuickEditModal } from '../components/ActionQuickEditModal'
import { IntelligenceInbox } from '../components/IntelligenceInbox'
import { Spinner } from '../components/Spinner'

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta']

interface ManagerGroup {
  managerId: string
  managerName: string
  cells: Map<number, Client[]> // turno (1-10) → clientes
}

export default function WeeklyPlanning() {
  const { data: clientsData, isLoading: loadingClients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list(),
  })
  const { data: actionsData, isLoading: loadingActions } = useQuery({
    queryKey: ['open-actions'],
    queryFn: () => actionsApi.open(),
  })

  const [editing, setEditing] = useState<OpenAction | null>(null)

  const clients = clientsData?.clients ?? []
  const actions = actionsData?.actions ?? []

  // Ações abertas por cliente.
  const openByClient = new Map<string, OpenAction[]>()
  for (const a of actions) {
    const list = openByClient.get(a.clientId) ?? []
    list.push(a)
    openByClient.set(a.clientId, list)
  }

  // Agrupa por gerente → célula (turno). Só clientes ativos com turno 1-10.
  const managers = new Map<string, ManagerGroup>()
  for (const c of clients) {
    if (!c.active || c.turno == null || c.turno < 1 || c.turno > 10) continue
    const mgr = c.primaryManager
    if (!mgr) continue
    let g = managers.get(mgr.id)
    if (!g) {
      g = { managerId: mgr.id, managerName: mgr.name, cells: new Map() }
      managers.set(mgr.id, g)
    }
    const cell = g.cells.get(c.turno) ?? []
    cell.push(c)
    g.cells.set(c.turno, cell)
  }
  // Ordena clientes dentro de cada célula por prioridade asc (null por último).
  for (const g of managers.values()) {
    for (const cell of g.cells.values()) {
      cell.sort((a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity))
    }
  }
  const managerGroups = [...managers.values()].sort((a, b) =>
    a.managerName.localeCompare(b.managerName)
  )

  const isLoading = loadingClients || loadingActions

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Planejamento Semanal</h1>

      {/* O que a IA quer confirmar com o gerente — 1 clique, antes de começar a semana. */}
      <IntelligenceInbox compact />

      {isLoading ? (
        <Spinner />
      ) : managerGroups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhum cliente posicionado. Defina o <strong>turno</strong> (1–10) dos clientes no cadastro
          para que apareçam aqui.
        </p>
      ) : (
        <div className="space-y-8">
          {managerGroups.map((g) => (
            <ManagerGrid
              key={g.managerId}
              group={g}
              openByClient={openByClient}
              onEdit={setEditing}
            />
          ))}
        </div>
      )}

      {editing && (
        <ActionQuickEditModal action={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  )
}

// Uma célula (turno) com seus clientes. showEmpty controla se mostra "—" quando
// vazia (desktop, para alinhar as colunas) ou se some (mobile, para compactar).
function Cell({
  clients,
  label,
  openByClient,
  onEdit,
  showEmpty,
}: {
  clients: Client[]
  label: string
  openByClient: Map<string, OpenAction[]>
  onEdit: (a: OpenAction) => void
  showEmpty?: boolean
}) {
  if (!showEmpty && clients.length === 0) return null
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="space-y-1.5">
        {clients.map((c) => (
          <ClientCard key={c.id} client={c} actions={openByClient.get(c.id) ?? []} onEdit={onEdit} />
        ))}
        {clients.length === 0 && <p className="text-[11px] text-slate-300">—</p>}
      </div>
    </div>
  )
}

function ManagerGrid({
  group,
  openByClient,
  onEdit,
}: {
  group: ManagerGroup
  openByClient: Map<string, OpenAction[]>
  onEdit: (a: OpenAction) => void
}) {
  const actionsOf = (id: string) => openByClient.get(id) ?? []

  // Esconde os dias (colunas) que não têm nenhuma ação aberta em nenhum turno.
  const visibleDays = [0, 1, 2, 3, 4].filter((d) =>
    [d * 2 + 1, d * 2 + 2].some((t) =>
      (group.cells.get(t) ?? []).some((c) => actionsOf(c.id).length > 0)
    )
  )

  return (
    <section>
      <h2 className="mb-2 text-base font-semibold text-slate-900">{group.managerName}</h2>
      {visibleDays.length === 0 ? (
        <p className="text-sm text-slate-400">
          Sem ações abertas para os clientes deste gerente.
        </p>
      ) : (
        <>
          {/* Desktop: grade horizontal (Seg–Sex em colunas) */}
          <div className="hidden overflow-x-auto pb-1 md:block">
            <div
              className="grid items-stretch gap-3"
              style={{ gridTemplateColumns: `repeat(${visibleDays.length}, minmax(200px, 1fr))` }}
            >
              {visibleDays.map((d) => (
                // Coluna do dia: mesma altura (grid stretch), teto de altura + scroll.
                <div key={d} className="flex max-h-[70vh] flex-col">
                  <div className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {DAYS[d]}
                  </div>
                  <div className="flex-1 space-y-2 overflow-y-auto pr-0.5">
                    <Cell clients={group.cells.get(d * 2 + 1) ?? []} label="Manhã" openByClient={openByClient} onEdit={onEdit} showEmpty />
                    <Cell clients={group.cells.get(d * 2 + 2) ?? []} label="Tarde" openByClient={openByClient} onEdit={onEdit} showEmpty />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: empilhado por dia (turnos vazios são ocultados) */}
          <div className="space-y-4 md:hidden">
            {visibleDays.map((d) => (
              <div key={d}>
                <h3 className="mb-1.5 border-b border-slate-100 pb-1 text-sm font-bold text-slate-800">
                  {DAYS[d]}
                </h3>
                <div className="space-y-2">
                  <Cell clients={group.cells.get(d * 2 + 1) ?? []} label="Manhã" openByClient={openByClient} onEdit={onEdit} />
                  <Cell clients={group.cells.get(d * 2 + 2) ?? []} label="Tarde" openByClient={openByClient} onEdit={onEdit} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

function ClientCard({
  client,
  actions,
  onEdit,
}: {
  client: Client
  actions: OpenAction[]
  onEdit: (a: OpenAction) => void
}) {
  const color = clientColor(client.id)
  return (
    <div className={`rounded-md border ${color.bg} ${color.text} ${color.border} p-2`}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-bold">{client.name}</span>
        {client.priority != null && (
          <span className="shrink-0 text-[10px] font-semibold opacity-70">P{client.priority}</span>
        )}
      </div>
      <ul className="mt-1 space-y-1">
        {actions.map((a) => (
          <li
            key={a.id}
            className="flex items-start justify-between gap-1 rounded bg-white/70 px-1.5 py-1"
          >
            <div className="min-w-0">
              <span className="text-[11px] leading-snug text-slate-700">{a.description}</span>
              {a.state.status === 'review' && a.state.reviewAt && (
                <span className="mt-0.5 inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">
                  Rever em {a.state.reviewAt.split('-').reverse().join('/')}
                </span>
              )}
            </div>
            <ActionIcons reportId={a.reportId} onEdit={() => onEdit(a)} />
          </li>
        ))}
        {actions.length === 0 && (
          <li className="text-[10px] opacity-60">sem ações abertas</li>
        )}
      </ul>
    </div>
  )
}
